import { useCallback, useEffect } from 'react';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { editionKeyToEdition, useTargetEdition } from './useTargetEdition.tsx';
import {
	getCcpEntitlementIdFromLicense,
	getCcpTransactionAccountIdFromLicense,
	getCurrentEditionFromLicense,
	useLicenseInformation,
} from './useLicenseInformation.tsx';
import { targetEditionToUpgradeOfferingId } from './useUpgradeEdition.tsx';
import { useDefaultOffering } from './useDefaultOffering.tsx';
import { useEntitlementInformation } from './useEntitlementInformation.tsx';

type UseCrossFlowEditionParityProps = {
	sourceProduct: string;
	targetProduct: string;
};

export const useCrossFlowEditionParityData = ({
	sourceProduct,
	targetProduct,
}: UseCrossFlowEditionParityProps) => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const productEntitlementDetails = useProductEntitlementDetails();
	const targetProductEntitlementDetails = productEntitlementDetails?.[targetProduct];
	const billingSourceSystem = targetProductEntitlementDetails?.billingSourceSystem;

	const fireErrorAnalytics = useCallback(
		(reason: string, attributes?: Record<string, unknown>) =>
			fireOperationalAnalytics(createAnalyticsEvent({}), 'useCrossFlowEditionParityData failed', {
				...attributes,
				reason,
				sourceProduct,
				targetProduct,
			}),
		[createAnalyticsEvent, sourceProduct, targetProduct],
	);

	const {
		data: targetEdition,
		loading: targetEditionLoading,
		error: targetEditionError,
	} = useTargetEdition({
		sourceProduct,
		targetProduct,
	});

	const {
		data: licenseInformation,
		loading: licenseInformationLoading,
		error: licenseInformationError,
	} = useLicenseInformation({
		cloudId,
	});

	const ccpEntitlementId = licenseInformationLoading
		? null
		: getCcpEntitlementIdFromLicense({
				licenseInformation,
				productKey: targetProduct,
			});
	const currentTargetEdition = licenseInformationLoading
		? null
		: getCurrentEditionFromLicense({
				licenseInformation,
				productKey: targetProduct,
			});

	const transactionAccountId = licenseInformationLoading
		? null
		: getCcpTransactionAccountIdFromLicense({
				licenseInformation,
				productKey: targetProduct,
			});

	const {
		data: entitlementDetails,
		loading: entitlementDetailsLoading,
		error: entitlementDetailsError,
	} = useEntitlementInformation({
		entitlementId: ccpEntitlementId,
	});

	const getTargetEditionToUpgradeOfferingId = useCallback(() => {
		if (
			targetEditionLoading ||
			targetEdition?.edition === 'free' ||
			targetEdition?.edition === currentTargetEdition
		) {
			return null;
		}
		try {
			return targetEditionToUpgradeOfferingId({
				productKey: targetProduct,
				targetEdition: editionKeyToEdition(targetEdition?.edition),
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('InvalidTargetEditionToUpgradeOfferingId', {
				errorMessage: error.message,
				targetEdition,
				targetProduct,
			});
			return null;
		}
	}, [
		targetEditionLoading,
		targetEdition,
		currentTargetEdition,
		targetProduct,
		fireErrorAnalytics,
	]);

	const offeringId = getTargetEditionToUpgradeOfferingId();

	const {
		data: defaultOffering,
		loading: defaultOfferingLoading,
		error: defaultOfferingError,
	} = useDefaultOffering({
		offeringId,
		entitlementId: ccpEntitlementId || '',
		transactionAccountId:
			getCcpTransactionAccountIdFromLicense({
				licenseInformation,
				productKey: targetProduct,
			}) || '',
		targetEdition: editionKeyToEdition(targetEdition?.edition),
	});

	useEffect(() => {
		if (billingSourceSystem && billingSourceSystem !== 'CCP') {
			fireErrorAnalytics('UnsupportedBillingSourceSystem', {
				billingSourceSystem,
				sourceProduct,
				targetProduct,
			});
		}
	}, [targetEdition, billingSourceSystem, fireErrorAnalytics, sourceProduct, targetProduct]);

	useEffect(() => {
		if (targetEditionError) {
			fireErrorAnalytics('TargetEditionError', {
				errorMessage: targetEditionError.message,
			});
		}
		if (licenseInformationError) {
			fireErrorAnalytics('LicenseInformationError', {
				errorMessage: licenseInformationError.message,
			});
		}
		if (entitlementDetailsError) {
			fireErrorAnalytics('EntitlementDetailsError', {
				errorMessage: entitlementDetailsError.message,
			});
		}
		if (defaultOfferingError) {
			fireErrorAnalytics('DefaultOfferingError', {
				errorMessage: defaultOfferingError.message,
			});
		}
	}, [
		targetEditionError,
		licenseInformationError,
		entitlementDetailsError,
		defaultOfferingError,
		fireErrorAnalytics,
	]);

	return {
		isLoading:
			targetEditionLoading ||
			licenseInformationLoading ||
			defaultOfferingLoading ||
			entitlementDetailsLoading,
		shouldUpgrade: !(
			targetEdition?.edition === 'free' ||
			targetEdition?.edition === currentTargetEdition ||
			targetEditionError ||
			licenseInformationError ||
			entitlementDetailsError ||
			defaultOfferingError ||
			!entitlementDetails ||
			!offeringId ||
			!defaultOffering ||
			!transactionAccountId
		),
		ccpEntitlementId,
		offeringId,
		targetEdition: targetEdition && editionKeyToEdition(targetEdition.edition),
		entitlementDetails,
		billingSourceSystem,
		defaultOffering,
		currentTargetEdition,
		transactionAccountId,
	};
};
